<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useConnectionTemplate } from "@/queries/connection-templates";
import { useConnection, useCreateConnection, useUpdateConnection } from "@/queries/connections";
import { useCredentials } from "@/queries/credentials";
import { useAlertStore } from "@/stores/alerts-store";
import { useSessionStore } from "@/stores/session-store";
import { credentialKindLabel } from "../utils/credentialDisplay";
import type { CredentialKind } from "@/types";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import BlockUI from "@/components/BlockUI.vue";
import FormAlert from "@/components/FormAlert.vue";
import type { TemplateAuthMethod } from "@/types";

const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();
const session = useSessionStore();

// Auth methods that hand the vendor a redirect_uri the user must pre-register with their OAuth app.
const REDIRECT_KINDS = ["oauth2-authcode", "vendor-initiated"];

const isEdit = computed(() => route.name === "connection-edit");
const templateId = computed(() => (route.params.templateId as string) ?? "");
const connectionId = computed(() => (route.params.connectionid as string) ?? "");

// ── Edit mode: existing connection ──────────────────────────────────────────────────────────
const { connection, connectionLoading, connectionError } = useConnection(connectionId);
const { updateConnection, updateConnectionPending, updateConnectionError, updateConnectionReset } = useUpdateConnection();

// ── Template: from the route in create mode, from the loaded connection in edit mode — both
// modes render the method's declared input fields and credential roles.
const templateIdForLoad = computed(() => (isEdit.value ? connection.value?.templateId ?? "" : templateId.value));
const { template, templateLoading, templateError } = useConnectionTemplate(templateIdForLoad);
const { createConnection, createConnectionPending, createConnectionError, createConnectionReset } = useCreateConnection();

const loading = computed(() =>
  isEdit.value ? connectionLoading.value || templateLoading.value : templateLoading.value);
const loadError = computed(() => (isEdit.value ? connectionError.value : templateError.value));
const saving = computed(() => createConnectionPending.value || updateConnectionPending.value);
const saveError = computed(() => createConnectionError.value || updateConnectionError.value);

const name = ref("");
const chosenId = ref("");
const inputs = ref<Record<string, string>>({});
const configDraft = ref<Record<string, string>>({});
const linkedDraft = ref<Record<string, string>>({});
// Edit-mode secret overlay: blank fields stay blank and are dropped on submit (keep-current),
// so we never round-trip stored secret material through the browser.
const secretDraft = ref<Record<string, string>>({});

// Default the chosen method once the template loads (single method → auto-select).
watch(template, (t) => {
  if (!t || isEdit.value) return;
  if (!name.value) name.value = t.name;
  if (!chosenId.value && t.authMethods.length > 0) chosenId.value = t.authMethods[0].id;
}, { immediate: true });

// Edit mode: seed name, config, the pinned method, and the current credential links. Secret fields
// start blank (keep-current); a value the user types overwrites just that secret.
watch(connection, (c) => {
  if (!c) return;
  name.value = c.name;
  chosenId.value = c.authMethodId;
  configDraft.value = { ...c.config };
  linkedDraft.value = { ...c.linkedCredentials };
  secretDraft.value = {};
}, { immediate: true });

const selectedMethod = computed<TemplateAuthMethod | undefined>(() =>
  template.value?.authMethods.find((m) => m.id === chosenId.value));

const inputFields = computed(() => selectedMethod.value?.fields.filter((f) => f.input) ?? []);
const multiMethod = computed(() => (template.value?.authMethods.length ?? 0) > 1);

// Template-declared credential roles (§4.3): one kind-filtered picker per role, required unless
// optional. Selections travel as linkedCredentials (role → credentialId) on the create request.
const credentialRoles = computed(() => selectedMethod.value?.credentials ?? []);
const { credentials: availableCredentials } = useCredentials();
// Switching methods resets the picks (create only — edit's method is pinned and seeds the draft).
watch(chosenId, () => { if (!isEdit.value) linkedDraft.value = {}; });

function credentialOptions(kind: string) {
  return availableCredentials.value.filter(
    (c) => c.status === "Active" && c.kind.toLowerCase() === kind.toLowerCase());
}
function roleKindLabel(kind: string): string {
  return credentialKindLabel(kind as CredentialKind);
}

const isAuthorizeOnly = computed(() =>
  !isEdit.value && !!selectedMethod.value && inputFields.value.length === 0 && credentialRoles.value.length === 0);

// Redirect-based methods need the environment's callback URL registered with the provider — show it
// (read-only) on the create form so the user can copy it across. Sourced once from the session.
const callbackUrl = computed(() => session.session.connectionCallbackUrl ?? null);
const showCallbackUrl = computed(() =>
  !isEdit.value && !!callbackUrl.value && REDIRECT_KINDS.includes(selectedMethod.value?.kind ?? ""));

async function copyCallbackUrl() {
  if (!callbackUrl.value) return;
  try {
    await navigator.clipboard.writeText(callbackUrl.value);
    alertStore.addSuccess("Copied", "Callback URL copied to clipboard.");
  } catch {
    alertStore.addError("Copy failed", "Select the field and copy manually.");
  }
}

const breadcrumbs = computed(() =>
  isEdit.value
    ? [{ label: "Dashboard", href: "/" }, { label: "Connections", href: "/connections" }, { label: "Edit" }]
    : [{ label: "Dashboard", href: "/" }, { label: "Connections", href: "/connections" }, { label: "New", href: "/connections/new" }, { label: template.value?.name ?? "Setup" }]);

function resetErrors() {
  createConnectionReset();
  updateConnectionReset();
}

function cancel() {
  // Return to wherever the user came from — the list, the detail view, or the template picker.
  router.back();
}

// Set when the user arrived from a project slot — used to route back and auto-bind on success.
const returnProjectId = computed(() => (route.query.returnProjectId as string) ?? "");
const returnSlotName = computed(() => (route.query.slotName as string) ?? "");

function submitCreate() {
  if (!template.value || !chosenId.value || !name.value.trim()) return;
  const linked = Object.fromEntries(Object.entries(linkedDraft.value).filter(([, id]) => !!id));
  createConnection(
    {
      templateId: template.value.id,
      authMethodId: chosenId.value,
      name: name.value.trim(),
      inputs: { ...inputs.value },
      linkedCredentials: Object.keys(linked).length > 0 ? linked : undefined,
    },
    {
      onSuccess: (result) => {
        // oauth2-authcode → follow the vendor redirect; the callback lands the connection Active.
        // The return-to-project context can't survive that round-trip, so OAuth keeps the default
        // landing; return-and-bind covers the synchronous kinds (apikey / basic / bearer / s2s).
        if (result.authorizeUrl) {
          window.location.href = result.authorizeUrl;
          return;
        }
        // Arrived from a project slot with a usable (synchronous) connection → return there and
        // hand off the id + slot so the tab binds it. Gated on setupCompleted so a not-yet-usable
        // kind (e.g. vendor-initiated) never auto-binds; the tab's own toast is the only success.
        if (result.setupCompleted && returnProjectId.value && returnSlotName.value) {
          router.push({
            name: "project-connections",
            params: { projectid: returnProjectId.value },
            query: { bound: result.connection.id, slotName: returnSlotName.value },
          });
          return;
        }
        if (result.setupCompleted) {
          alertStore.addSuccess("Connected", `${result.connection.name} is ready to use.`);
        }
        router.push(`/connections/${result.connection.id}`);
      },
      // errors surface via saveError → FormAlert
    });
}

function submitEdit() {
  if (!connection.value || !name.value.trim()) return;
  // Full-replace set, restricted to declared roles — a link whose role the template no longer
  // declares would be rejected by the API, so dropping it here reconciles the connection instead.
  const declaredRoles = new Set(credentialRoles.value.map((r) => r.role));
  const linked = Object.fromEntries(
    Object.entries(linkedDraft.value).filter(([role, id]) => !!id && declaredRoles.has(role)));
  // Only re-typed secrets travel; blanks keep the stored value.
  const secrets = Object.fromEntries(
    Object.entries(secretDraft.value).filter(([, v]) => v !== ""));
  updateConnection(
    {
      id: connection.value.id,
      data: {
        name: name.value.trim(),
        config: { ...configDraft.value },
        tags: connection.value.tags,
        linkedCredentials: linked,
        secrets: Object.keys(secrets).length > 0 ? secrets : undefined,
      },
    },
    {
      onSuccess: () => {
        alertStore.addSuccess("Saved", "Connection updated.");
        router.push(`/connections/${connectionId.value}`);
      },
    });
}

function onSubmit() {
  if (isEdit.value) submitEdit();
  else submitCreate();
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <template v-if="loading">
          <SkeletonLoader type="header" />
          <SkeletonLoader type="form" :fields="4" class="mt-4" />
        </template>

        <template v-else>
          <PageHeader
            :title="isEdit ? 'Edit Connection' : `New ${template?.name ?? ''} Connection`"
            :breadcrumbs="breadcrumbs"
          />

          <div v-if="loadError" class="alert alert-danger">
            Failed to load. <a href="#" @click.prevent="cancel">Go back</a>.
          </div>

          <BlockUI v-else :active="saving">
            <FormAlert :error="saveError" @dismiss="resetErrors" />

            <form @submit.prevent="onSubmit">
              <!-- Connection name -->
              <div class="row mb-3">
                <label for="conn-name" class="col-sm-3 col-form-label">
                  Name <span class="text-danger">*</span>
                </label>
                <div class="col-sm-9">
                  <input id="conn-name" v-model="name" type="text" class="form-control" required />
                </div>
              </div>

              <!-- CREATE: auth-method chooser (only when the template offers more than one) -->
              <template v-if="!isEdit">
                <div v-if="multiMethod" class="row mb-3">
                  <label for="auth-method" class="col-sm-3 col-form-label">
                    Authentication <span class="text-danger">*</span>
                  </label>
                  <div class="col-sm-9">
                    <select id="auth-method" v-model="chosenId" class="form-select">
                      <option v-for="m in template?.authMethods" :key="m.id" :value="m.id">
                        {{ m.label ?? m.kind }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Method input fields (input: true). Secret fields render as password inputs. -->
                <div v-for="f in inputFields" :key="f.name" class="row mb-3">
                  <label :for="`f-${f.name}`" class="col-sm-3 col-form-label text-capitalize">{{ f.name }}</label>
                  <div class="col-sm-9">
                    <input
                      :id="`f-${f.name}`"
                      v-model="inputs[f.name]"
                      :type="f.secret ? 'password' : 'text'"
                      class="form-control"
                      :autocomplete="f.secret ? 'new-password' : 'off'"
                    />
                  </div>
                </div>

                <div v-if="isAuthorizeOnly" class="row mb-3">
                  <div class="col-sm-9 offset-sm-3">
                    <p class="text-muted mb-0">No details needed — continue to authorize with the provider.</p>
                  </div>
                </div>

                <!-- Redirect URI to register with the provider (oauth2-authcode / vendor-initiated). -->
                <div v-if="showCallbackUrl" class="row mb-3">
                  <label for="callback-url" class="col-sm-3 col-form-label">Callback URL</label>
                  <div class="col-sm-9">
                    <div class="input-group">
                      <input id="callback-url" :value="callbackUrl" type="text" class="form-control" readonly />
                      <button type="button" class="bb-btn-secondary-cta text-nowrap" @click="copyCallbackUrl">
                        <i class="bi bi-clipboard me-1"></i>Copy
                      </button>
                    </div>
                    <div class="form-text">Register this as the redirect URI in your provider's OAuth app.</div>
                  </div>
                </div>
              </template>

              <!-- EDIT: the method's declared input fields (skipped ones included). Non-secret
                   fields are prefilled; secret fields start blank and only overwrite when filled.
                   Engine-written config is preserved untouched. -->
              <template v-else>
                <div v-for="f in inputFields" :key="f.name" class="row mb-3">
                  <label :for="`f-${f.name}`" class="col-sm-3 col-form-label text-capitalize">{{ f.name }}</label>
                  <div class="col-sm-9">
                    <input
                      v-if="f.secret"
                      :id="`f-${f.name}`"
                      v-model="secretDraft[f.name]"
                      type="password"
                      class="form-control"
                      autocomplete="new-password"
                      placeholder="Leave blank to keep current"
                    />
                    <input v-else :id="`f-${f.name}`" v-model="configDraft[f.name]" type="text" class="form-control" />
                  </div>
                </div>
              </template>

              <!-- Template-declared credential roles (create picks, edit relinks): kind-filtered
                   picker per role. -->
              <div v-for="cr in credentialRoles" :key="cr.role" class="row mb-3">
                <label :for="`cred-${cr.role}`" class="col-sm-3 col-form-label">
                  {{ cr.label ?? cr.role }} <span v-if="!cr.optional" class="text-danger">*</span>
                </label>
                <div class="col-sm-9">
                  <select
                    :id="`cred-${cr.role}`"
                    v-model="linkedDraft[cr.role]"
                    class="form-select"
                    :required="!cr.optional"
                  >
                    <option value="">{{ cr.optional ? "None" : `Select a ${roleKindLabel(cr.kind).toLowerCase()}…` }}</option>
                    <option v-for="c in credentialOptions(cr.kind)" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                  <div v-if="credentialOptions(cr.kind).length === 0" class="form-text">
                    No {{ roleKindLabel(cr.kind).toLowerCase() }} credentials yet —
                    <router-link to="/connections/credentials">add one under Credentials</router-link>.
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div class="row">
                <div class="col-sm-9 offset-sm-3">
                  <div class="d-flex justify-content-end gap-2">
                    <button type="button" class="bb-btn-cancel" @click="cancel">Cancel</button>
                    <button type="submit" class="bb-btn-primary-cta" :disabled="saving">
                      <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
                      {{ isEdit ? "Save Changes" : (isAuthorizeOnly ? "Authorize" : "Create") }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </BlockUI>
        </template>
      </PanelCard>
    </PanelGrid>
  </main>
</template>
